// src/hooks/notifications/useFightThreadUpdates.js
import { useState, useEffect, useCallback } from "react";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";
import { useSharedEventBridgeNotifications } from "./useSharedEventBridgeNotifications";

/**
 * Hook for listening to real-time updates for a specific fight thread
 * Used by components like FightDiscussionIcon to show live message counts
 */
export const useFightThreadUpdates = (fightId, initialMessageCount = null) => {
  const [messageCount, setMessageCount] = useState(initialMessageCount);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Use shared EventBridge notifications
  const { notifications } = useSharedEventBridgeNotifications();

  // Handle thread notifications for this specific fight
  const handleFightThreadNotification = useCallback(
    (notification) => {
      if (!ThreadNotificationHandler.isThreadNotification(notification)) {
        return;
      }

      const { data } = notification;
      const notificationFightId = data.metadata?.fightId;

      // Only process notifications for this specific fight
      if (!notificationFightId || notificationFightId !== fightId) {
        return;
      }

      // Update message count based on notification type
      switch (data.type) {
        case "message.replied":
          // New reply increases the thread message count
          if (data.metadata?.threadMessageCount !== undefined) {
            setMessageCount(data.metadata.threadMessageCount);
            setLastUpdate(Date.now());
          } else {
            // Fallback: increment current count
            setMessageCount((prev) => (prev ? prev + 1 : 1));
            setLastUpdate(Date.now());
          }
          break;
        case "message.liked":
          // Likes don't change message count, but we track the update
          setLastUpdate(Date.now());
          break;
        default:
        // Unknown notification type
      }
    },
    [fightId]
  );

  // Listen for EventBridge notifications
  useEffect(() => {
    if (notifications && notifications.length > 0) {
      // Process the most recent notification
      const latestNotification = notifications[0];
      if (ThreadNotificationHandler.isThreadNotification(latestNotification)) {
        handleFightThreadNotification(latestNotification);
      }
    }
  }, [notifications, handleFightThreadNotification]);

  // Update initial message count when it changes
  useEffect(() => {
    if (initialMessageCount !== null && initialMessageCount !== messageCount) {
      setMessageCount(initialMessageCount);
    }
  }, [initialMessageCount, messageCount]);

  return {
    messageCount,
    lastUpdate,
    hasUpdates: lastUpdate !== null,
  };
};

export default useFightThreadUpdates;
